/**
 * UserTable Component
 * Renders the users table with sorting, pagination, selection, and visible action buttons.
 * Extracted from UsersManagement to keep files under 500 lines.
 */

import { Check, X, Mail, Eye, Power, Shield, UserCheck, Building2, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { UserProfile } from '../../lib/api';

export type SortField = 'name' | 'email' | 'company' | 'role' | 'created_at' | 'last_login';
export type SortDirection = 'asc' | 'desc';

interface UserTableProps {
  /** Paginated list of users to display */
  users: UserProfile[];
  /** Set of selected user IDs */
  selectedUsers: Set<number>;
  /** Toggle selection for a single user */
  onToggleSelection: (id: number) => void;
  /** Toggle select-all for current page */
  onToggleSelectAll: () => void;
  /** Open user details modal */
  onViewDetails: (user: UserProfile) => void;
  /** Toggle user active/inactive */
  onToggleActive: (user: UserProfile) => void;
  /** Open change role dialog */
  onChangeRole: (user: UserProfile) => void;
  /** Send invitation email to user */
  onSendInvitation: (user: UserProfile) => void;
  /** Approve a pending user */
  onApprove: (userId: number) => void;
  /** CR-5312: open the delete-or-deactivate confirm flow for a user. */
  onSafeDelete: (user: UserProfile) => void;
  /** View activity log for a user */
  onViewActivity: (user: UserProfile) => void;
  /** Check if user is the current logged-in user */
  isSelf: (user: UserProfile) => boolean;
  /** Table is loading data */
  loading: boolean;
  /** An action is in progress */
  actionLoading: boolean;
  /** Current sort field */
  sortField: SortField;
  /** Current sort direction */
  sortDirection: SortDirection;
  /** Handle sort column click */
  onSort: (field: SortField) => void;
  /** Current page number */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total filtered count (before pagination) */
  totalFiltered: number;
  /** Handle page change */
  onPageChange: (page: number) => void;
}

function SortIndicator({ field, sortField, sortDirection }: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) {
  if (sortField !== field) return null;
  return <span className="ml-1">{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>;
}

/**
 * User management table with inline action buttons (VIC-style).
 * Displays user data with sorting, selection checkboxes, and per-row visible actions.
 */
export default function UserTable({
  users,
  selectedUsers,
  onToggleSelection,
  onToggleSelectAll,
  onViewDetails,
  onToggleActive,
  onChangeRole,
  onSendInvitation,
  onApprove,
  onSafeDelete,
  onViewActivity,
  isSelf,
  loading,
  actionLoading,
  sortField,
  sortDirection,
  onSort,
  currentPage,
  totalPages,
  totalFiltered,
  onPageChange,
}: UserTableProps) {
  const sortableHead = (label: string, field: SortField) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => onSort(field)}
    >
      {label}
      <SortIndicator field={field} sortField={sortField} sortDirection={sortDirection} />
    </TableHead>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border rounded-lg">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      users.length > 0 && users.every((u) => selectedUsers.has(u.id))
                    }
                    onCheckedChange={onToggleSelectAll}
                  />
                </TableHead>
                {sortableHead('Email', 'email')}
                {sortableHead('Name', 'name')}
                {sortableHead('Company', 'company')}
                {sortableHead('Role', 'role')}
                {sortableHead('Last login', 'last_login')}
                <TableHead>Status</TableHead>
                <TableHead>Invitation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((userProfile) => (
                  <UserTableRow
                    key={userProfile.id}
                    userProfile={userProfile}
                    selected={selectedUsers.has(userProfile.id)}
                    onToggleSelection={onToggleSelection}
                    onViewDetails={onViewDetails}
                    onToggleActive={onToggleActive}
                    onChangeRole={onChangeRole}
                    onSendInvitation={onSendInvitation}
                    onApprove={onApprove}
                    onSafeDelete={onSafeDelete}
                    onViewActivity={onViewActivity}
                    isSelf={isSelf(userProfile)}
                    actionLoading={actionLoading}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({totalFiltered} users)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

// --- Individual Row Component ---

interface UserTableRowProps {
  userProfile: UserProfile;
  selected: boolean;
  onToggleSelection: (id: number) => void;
  onViewDetails: (user: UserProfile) => void;
  onToggleActive: (user: UserProfile) => void;
  onChangeRole: (user: UserProfile) => void;
  onSendInvitation: (user: UserProfile) => void;
  onApprove: (userId: number) => void;
  onSafeDelete: (user: UserProfile) => void;
  onViewActivity: (user: UserProfile) => void;
  isSelf: boolean;
  actionLoading: boolean;
}

function UserTableRow({
  userProfile,
  selected,
  onToggleSelection,
  onViewDetails,
  onToggleActive,
  onChangeRole,
  onSendInvitation,
  onApprove,
  onSafeDelete,
  isSelf,
  actionLoading,
}: UserTableRowProps) {
  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell>
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelection(userProfile.id)}
        />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {userProfile.user.email}
      </TableCell>
      <TableCell className="font-medium">{userProfile.user.full_name}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm">{userProfile.company_name}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">{userProfile.role_name}</Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
        {userProfile.user.last_login
          ? new Date(userProfile.user.last_login).toLocaleString()
          : '—'}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {/* CR-4551 round 4: show "Invite Sent" while the user is still
              an unaccepted placeholder so the Status column matches the
              supplier-detail Users tab card. */}
          {!userProfile.user.is_active ? (
            <Badge variant="secondary" className="text-xs">
              <X className="h-3 w-3 mr-1" />Inactive
            </Badge>
          ) : userProfile.user.invitation_sent && !userProfile.user.has_joined ? (
            <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
              Invite Sent
            </Badge>
          ) : (
            <Badge variant="default" className="bg-green-500 text-xs">
              <Check className="h-3 w-3 mr-1" />Active
            </Badge>
          )}
          {!userProfile.is_approved && (
            <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
              Pending
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <InvitationBadge userProfile={userProfile} />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-0.5">
          <ActionButton
            tooltip="View Details"
            onClick={() => onViewDetails(userProfile)}
            disabled={actionLoading}
          >
            <Eye className="h-4 w-4" />
          </ActionButton>
          <ActionButton
            tooltip="Send Invitation"
            onClick={() => onSendInvitation(userProfile)}
            disabled={actionLoading}
          >
            <Mail className="h-4 w-4" />
          </ActionButton>
          <ActionButton
            tooltip={userProfile.user.is_active ? 'Disable User' : 'Enable User'}
            onClick={() => onToggleActive(userProfile)}
            disabled={actionLoading || isSelf}
          >
            <Power className={`h-4 w-4 ${userProfile.user.is_active ? 'text-red-500' : 'text-green-500'}`} />
          </ActionButton>
          <ActionButton
            tooltip="Change Role"
            onClick={() => onChangeRole(userProfile)}
            disabled={actionLoading || isSelf}
          >
            <Shield className="h-4 w-4" />
          </ActionButton>
          {!userProfile.is_approved && (
            <ActionButton
              tooltip="Approve User"
              onClick={() => onApprove(userProfile.id)}
              disabled={actionLoading}
            >
              <UserCheck className="h-4 w-4 text-green-600" />
            </ActionButton>
          )}
          {/* CR-5312: parent confirms then routes to delete vs. deactivate
              based on whether the user has any login / audit footprint. */}
          <ActionButton
            tooltip="Delete or deactivate"
            onClick={() => onSafeDelete(userProfile)}
            disabled={actionLoading || isSelf}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </ActionButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

// --- Helper Components ---

function InvitationBadge({ userProfile }: { userProfile: UserProfile }) {
  if (userProfile.user.has_joined) {
    return (
      <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-xs">
        Joined
      </Badge>
    );
  }
  if (userProfile.user.invitation_sent) {
    return (
      <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 text-xs">
        <Mail className="h-3 w-3 mr-1" />Sent
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-gray-500 border-gray-300 text-xs">
      Not Invited
    </Badge>
  );
}

function ActionButton({
  tooltip,
  onClick,
  disabled,
  children,
}: {
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClick}
          disabled={disabled}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
